import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { Order } from '@/types/order'
import { getInspectionLead } from '@/api/inspection-leads'
import { refundLeadPayment } from '@/api/tbank'
import { Button } from '@/components/ui/Button'

const card =
  'rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-4'

const REFUND_REASON_KEYS: Record<string, string> = {
  not_paid: 'orderBlocks.refund.reason.notPaid',
  already_refunded: 'orderBlocks.refund.reason.alreadyRefunded',
  no_payment_id: 'orderBlocks.refund.reason.noPaymentId',
  lead_not_found: 'orderBlocks.refund.reason.leadNotFound',
  forbidden: 'orderBlocks.refund.reason.forbidden',
  invoke_failed: 'orderBlocks.refund.reason.invokeFailed',
  no_response: 'orderBlocks.refund.reason.noResponse',
  missing_secrets: 'orderBlocks.refund.reason.missingSecrets',
  order_completed: 'orderBlocks.refund.reason.orderCompleted',
}

/** Человеческий текст ошибки возврата из структурированного ответа edge-функции. */
function refundErrorText(
  data: {
    reason?: string
    tbankMessage?: string
    tbankErrorCode?: string
    tbankDetails?: string
    detail?: string
  },
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (data.reason === 'tbank_cancel_failed') {
    const msg = data.tbankMessage || data.tbankDetails || data.detail
    const code = data.tbankErrorCode ? t('orderBlocks.refund.tbankCode', { code: data.tbankErrorCode }) : ''
    const tail = msg ? `: ${msg}` : '.'
    return t('orderBlocks.refund.tbankRejected', { code }) + tail
  }
  return (
    (data.reason && REFUND_REASON_KEYS[data.reason] && t(REFUND_REASON_KEYS[data.reason])) ||
    data.tbankMessage ||
    data.detail ||
    data.reason ||
    t('orderBlocks.refund.unknownError')
  )
}

/**
 * Блок «Платёж» на карточке B2C-заказа (individual). Тянет платёж из
 * связанного лида (order.lead_id) и даёт «Вернуть оплату» прямо из заказа —
 * диспетчер живёт в заказе после конвертации. Возврат каскадно отменяет заказ
 * (бэкенд: tbank-refund-payment / webhook REFUNDED).
 *
 * Рендерит null для B2B / без лида / неоплаченных — безопасно вставлять всегда.
 */
export function IndividualOrderRefund({ order }: { order: Order }) {
  const { t, i18n } = useTranslation()
  const qc = useQueryClient()
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const leadId = order.lead_id ?? null

  const { data: lead } = useQuery({
    queryKey: ['inspection-lead', leadId],
    queryFn: () => getInspectionLead(leadId!),
    enabled: order.customer_kind === 'individual' && !!leadId,
  })

  const refundMut = useMutation({
    mutationFn: () => refundLeadPayment(leadId!),
    onSuccess: (data) => {
      if (!data.ok) {
        setError(refundErrorText(data, t))
        return
      }
      setError(null)
      setConfirming(false)
      void qc.invalidateQueries({ queryKey: ['order', order.id] })
      void qc.invalidateQueries({ queryKey: ['orders'] })
      void qc.invalidateQueries({ queryKey: ['inspection-lead', leadId] })
    },
    onError: (e: Error) => setError(e.message || 'invoke_failed'),
  })

  if (order.customer_kind !== 'individual' || !leadId || !lead || !lead.payment_at) {
    return null
  }

  const locale = i18n.language === 'en' ? 'en-US' : 'ru-RU'
  const isRefunded = !!lead.refunded_at
  const amount =
    lead.payment_amount != null
      ? new Intl.NumberFormat(locale).format(Number(lead.payment_amount)) + ' ₽'
      : '—'

  return (
    <section className={card}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="stryzh-heading-card">{t('orderBlocks.refund.title')}</h2>
        <span
          className="rounded-full px-3 py-1 text-xs font-semibold"
          style={
            isRefunded
              ? { background: 'var(--stryzh-color-bg-elev)', color: 'var(--stryzh-color-text-secondary)' }
              : { background: 'rgba(74,222,128,.15)', color: 'var(--stryzh-color-success, #4ade80)' }
          }
        >
          {isRefunded ? t('orderBlocks.refund.badgeRefunded') : t('orderBlocks.refund.badgePaid')}
        </span>
      </div>

      <div className="text-sm text-[color:var(--stryzh-color-text-primary)]">
        {t('orderBlocks.refund.amount')} <span className="font-mono">{amount}</span>
        {isRefunded && lead.refunded_at ? (
          <span className="ml-2 text-xs text-[color:var(--stryzh-color-text-secondary)]">
            {t('orderBlocks.refund.refundedAt', { time: new Date(lead.refunded_at).toLocaleString(locale) })}
          </span>
        ) : null}
      </div>

      {/* Заказ COMPLETED — услуга оказана, возврат запрещён (бэкенд
       *  тоже возвращает 409 order_completed). Показываем подсказку
       *  вместо кнопки. */}
      {!isRefunded && lead.tbank_payment_id && order.status === 'COMPLETED' ? (
        <div className="mt-4 border-t border-[color:var(--stryzh-color-border-default)] pt-4 text-xs text-[color:var(--stryzh-color-text-secondary)]">
          {t('orderBlocks.refund.locked')}
        </div>
      ) : !isRefunded && lead.tbank_payment_id ? (
        <div className="mt-4 border-t border-[color:var(--stryzh-color-border-default)] pt-4">
          {!confirming ? (
            <Button variant="secondary" onClick={() => setConfirming(true)}>
              {t('orderBlocks.refund.refundBtn')}
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-[color:var(--stryzh-color-text-secondary)]">
                {t('orderBlocks.refund.confirmPre')}
                <strong>{amount}</strong>
                {t('orderBlocks.refund.confirmPost')}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  disabled={refundMut.isPending}
                  onClick={() => {
                    setError(null)
                    refundMut.mutate()
                  }}
                >
                  {refundMut.isPending ? t('orderBlocks.refund.refunding') : t('orderBlocks.refund.confirmBtn')}
                </Button>
                <Button
                  variant="ghost"
                  disabled={refundMut.isPending}
                  onClick={() => {
                    setConfirming(false)
                    setError(null)
                  }}
                >
                  {t('orderBlocks.refund.cancel')}
                </Button>
              </div>
              {error ? (
                <div className="rounded-md border border-[color:var(--stryzh-color-accent)] bg-[rgba(255,106,26,0.1)] px-3 py-2 text-xs text-[color:var(--stryzh-color-accent)]">
                  {error}
                </div>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </section>
  )
}
